import fs from 'node:fs';
import path from 'node:path';

export interface HtmlAssetValidationOptions {
  root: string;
  pagesDir: string;
  html: string;
  pluginName: string;
  pageLabel?: string;
  missingAssets?: 'error' | 'warn';
  externalAssets?: string | string[];
}

function stripQueryAndHash(url: string): string {
  return url.split('#')[0].split('?')[0];
}

function isLocalRootUrl(url: string): boolean {
  return !!url && url.startsWith('/') && !url.startsWith('//');
}

/**
 * URLs another plugin serves, which therefore have no file on disk here.
 *
 * A trailing slash makes an entry a directory prefix (`/su/` covers
 * `/su/alert.js`); anything else has to match the URL exactly, so `/su`
 * never quietly swallows `/super.js`.
 */
function matchesExternalAsset(url: string, patterns: string[]): boolean {
  const clean = stripQueryAndHash(url);

  return patterns.some((pattern) =>
    pattern.endsWith('/') ? clean.startsWith(pattern) : clean === pattern,
  );
}

function normalizeExternalAssets(value?: string | string[]): string[] {
  const list = value == null ? [] : Array.isArray(value) ? value : [value];

  // Root-relative is the only thing a page can reference this way, so a
  // caller who left the slash off meant the same thing.
  return list
    .filter(Boolean)
    .map((entry) => (entry.startsWith('/') ? entry : `/${entry}`));
}

function fileExistsForPublicUrl(root: string, pagesDir: string, url: string): boolean {
  const clean = stripQueryAndHash(url).slice(1);

  const fromSrc = path.join(root, pagesDir, clean);
  if (fs.existsSync(fromSrc)) return true;

  const fromPublic = path.join(root, 'public', clean);
  if (fs.existsSync(fromPublic)) return true;

  return false;
}

function collectScriptSrcs(html: string): string[] {
  const out: string[] = [];

  for (const match of html.matchAll(
    /<script\b[^>]*\bsrc=["']([^"']+)["'][^>]*>/gi,
  )) {
    out.push(match[1]);
  }

  return out;
}

function collectStylesheetHrefs(html: string): string[] {
  const out: string[] = [];

  // Match attributes independently so <link href=".." rel="stylesheet">
  // is detected regardless of attribute order.
  for (const match of html.matchAll(/<link\b[^>]*>/gi)) {
    const tag = match[0];

    if (!/\brel=["']?stylesheet["']?/i.test(tag)) continue;

    const href = tag.match(/\bhref=["']([^"']+)["']/i);

    if (href) out.push(href[1]);
  }

  return out;
}

/** Drop documentation samples so example `import('/…')` strings aren't treated as assets. */
function stripCodeSampleMarkup(html: string): string {
  return html
    .replace(/<pre\b[^>]*>[\s\S]*?<\/pre>/gi, '')
    .replace(/<code\b[^>]*>[\s\S]*?<\/code>/gi, '');
}

function collectLiteralDynamicImports(html: string): string[] {
  const out: string[] = [];
  const withoutSamples = stripCodeSampleMarkup(html);

  for (const match of withoutSamples.matchAll(
    /import\s*\(\s*["']([^"'`]+)["']\s*\)/gi,
  )) {
    out.push(match[1]);
  }

  return out;
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}

function collectHrefSrcUrls(html: string): string[] {
  const out: string[] = [];

  for (const match of html.matchAll(/\b(?:href|src)=["']([^"']+)["']/gi)) {
    out.push(match[1]);
  }

  return out;
}

/**
 * Collects every root-relative URL the HTML references (any href/src
 * attribute plus literal dynamic imports), with query/hash stripped.
 * Used to decide which source assets must be emitted at build time.
 */
export function collectLocalAssetUrls(html: string): string[] {
  const candidates = [
    ...collectHrefSrcUrls(html),
    ...collectLiteralDynamicImports(html),
  ];

  return unique(candidates.filter(isLocalRootUrl).map(stripQueryAndHash));
}

function formatPageLabel(pageLabel?: string): string {
  return pageLabel ? ` (${pageLabel})` : '';
}

function missingAssetMessage(args: {
  pluginName: string;
  kind: string;
  url: string;
  root: string;
  pagesDir: string;
  pageLabel?: string;
}): string {
  const { pluginName, kind, url, root, pagesDir, pageLabel } = args;
  const clean = stripQueryAndHash(url).slice(1);
  const pageSuffix = formatPageLabel(pageLabel);

  return (
    `[${pluginName}] Missing ${kind}${pageSuffix}: ${url}\n` +
    `Expected one of:\n` +
    `- ${path.join(root, pagesDir, clean)}\n` +
    `- ${path.join(root, 'public', clean)}`
  );
}

function reportMissing(args: {
  mode: 'error' | 'warn';
  pluginName: string;
  kind: string;
  url: string;
  root: string;
  pagesDir: string;
  pageLabel?: string;
}) {
  const message = missingAssetMessage(args);

  if (args.mode === 'warn') {
    console.warn(`⚠️ ${message}`);
    return;
  }

  throw new Error(message);
}

export function validateHtmlAssetReferences(
  options: HtmlAssetValidationOptions,
): void {
  const {
    root,
    pagesDir,
    html,
    pluginName,
    pageLabel,
    missingAssets = 'error',
    externalAssets,
  } = options;

  /*
   * An asset another plugin serves has no file under `pagesDir` or
   * `public/` to find, so checking for one only ever produces a false
   * report. Applied to all three kinds: whatever serves the URL serves
   * it whether a page reached it by `src`, `href`, or `import()`.
   */
  const external = normalizeExternalAssets(externalAssets);
  const isCheckable = (url: string) =>
    isLocalRootUrl(url) && !matchesExternalAsset(url, external);

  const scriptSrcs = unique(collectScriptSrcs(html)).filter(isCheckable);
  const stylesheetHrefs = unique(collectStylesheetHrefs(html)).filter(isCheckable);
  const literalDynamicImports = unique(collectLiteralDynamicImports(html)).filter(
    isCheckable,
  );

  for (const url of scriptSrcs) {
    if (!fileExistsForPublicUrl(root, pagesDir, url)) {
      reportMissing({
        mode: missingAssets,
        pluginName,
        kind: 'JavaScript asset',
        url,
        root,
        pagesDir,
        pageLabel,
      });
    }
  }

  for (const url of stylesheetHrefs) {
    if (!fileExistsForPublicUrl(root, pagesDir, url)) {
      reportMissing({
        mode: missingAssets,
        pluginName,
        kind: 'stylesheet asset',
        url,
        root,
        pagesDir,
        pageLabel,
      });
    }
  }

  for (const url of literalDynamicImports) {
    if (!fileExistsForPublicUrl(root, pagesDir, url)) {
      console.warn(
        `⚠️ ${missingAssetMessage({
          pluginName,
          kind: 'literal dynamic import',
          url,
          root,
          pagesDir,
          pageLabel,
        })}`,
      );
    }
  }
}